/**
 * Wire contract between the robot, the relay and the panel.
 *
 * Imported by BOTH server/index.ts and the React client, so the state machine
 * exists exactly once: the relay uses `applyEvent` to keep the state it replays
 * to late joiners, and the panel uses the same function to fold incoming events
 * into its React state. They can never drift.
 */

export const PROTOCOL_VERSION = 3;

/** Hard ceiling on tiles in a dashboard -- more than this and nothing is readable. */
export const MAX_TILES = 6;

/**
 * v2: the relay holds one folded `DisplayState` per robot, not one global one.
 * Both `/robot` and `/panel` sockets carry `?robot=<id>` to pick their room.
 *
 * A socket with no `?robot=` lands in `DEFAULT_ROOM` -- the v1 single-room
 * behaviour, kept as a compatibility window so a screen or a publisher built
 * against v1 keeps working until it ships a keyed client.
 *
 * v3: a room carries its `ScreenConfig` -- what the wall says when idle, its
 * brand colours, its tile ceiling, its pacing. Purely additive, so no
 * compatibility window: a v2 panel ignores the extra `config` on `sync` and
 * falls through `applyEvent`'s default on `screen.config`, which is exactly the
 * old behaviour. The version still moves, because the contract did.
 */
export const DEFAULT_ROOM = "default";

/** Robot ids on the wire: a Prisma cuid, or a hand-set id like `rm-01`. */
const ROOM_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Normalise an untrusted `?robot=` value into a room key.
 *   - absent / empty  -> `DEFAULT_ROOM` (the legacy path)
 *   - well-formed id   -> that id
 *   - present but junk -> `null`, so the caller can reject rather than quietly
 *     merge a mistyped screen into the shared room
 */
export function roomIdFromQuery(raw: unknown): string | null {
  if (raw === undefined || raw === null || raw === "") return DEFAULT_ROOM;
  const s = String(raw).trim();
  if (s === "") return DEFAULT_ROOM;
  return ROOM_ID_RE.test(s) ? s : null;
}

/* ------------------------------------------------------- screen config (v3) */

export const TEXT_DIRECTIONS = ["rtl", "ltr", "auto"] as const;
export type TextDirection = (typeof TEXT_DIRECTIONS)[number];

/**
 * What a screen looks like and how it paces itself, as opposed to what is
 * currently on it.
 *
 * Every field here was a literal in the client until v3, which meant a second
 * customer's wall greeted their visitors in Arabic as Tatweer. It is delivered
 * over the same socket as the content -- the panel stays same-origin and
 * fetches nothing -- and the gateway is its only author. The robot cannot set
 * it: a model that can rewrite the headline of the screen it is presenting on
 * is a model that will.
 *
 * Content, not config: what is on screen right now. That is `DisplayEvent`, it
 * comes from the robot, and it is deliberately not persisted anywhere.
 */
export interface ScreenConfig {
  /* --- the idle screen: what the wall says when nobody is presenting ------ */

  /** The big line. Empty falls back to nothing rather than to a default brand. */
  headline: string;
  /** The paragraph under it -- what this robot can be asked for. */
  subheadline: string;
  /** Pills under the paragraph: the brands or departments this screen fronts. */
  chips: string[];
  /** The "try saying ..." prompt. Empty hides the line. */
  hint: string;
  /** Absolute URL of a logo. Empty keeps the animated pulse. */
  logoUrl: string;

  /* --- theme ------------------------------------------------------------- */

  /** Primary brand colour, `#rrggbb`. Drives the gradient and the tile accents. */
  accent: string;
  /** Second stop of the same gradient. */
  accent2: string;
  /**
   * `auto` lets each string pick its own direction, which is right for a
   * bilingual room. A fixed value is for a wall that must never reflow.
   */
  direction: TextDirection;

  /* --- layout ------------------------------------------------------------ */

  /**
   * Per-screen tile ceiling, clamped to `MAX_TILES`.
   *
   * Lower than the fleet ceiling is a real case: a 24" panel by a lift shows
   * three tiles legibly and six illegibly, and the model has no way to know
   * which room it is in.
   */
  maxTiles: number;
  /** Heading used when the model begins a dashboard without naming one. */
  dashboardFallbackTitle: string;

  /* --- behaviour --------------------------------------------------------- */

  /**
   * Milliseconds between tiles as a dashboard builds.
   *
   * Gemini emits an entire dashboard's `add_tile` calls within milliseconds of
   * each other, so without pacing "build it one by one" is a single pop.
   */
  revealMs: number;
  /**
   * Seconds of no events before the screen returns to idle by itself. `0`
   * never clears -- which is the right answer for a boardroom and the wrong one
   * for a lobby, where last Tuesday's dashboard is still up on Friday.
   */
  idleAfterSec: number;
  /** The connection dot. Off for a public wall, on for anything being debugged. */
  showStatus: boolean;
}

export const DEFAULT_SCREEN_CONFIG: ScreenConfig = {
  headline: "",
  subheadline: "",
  chips: [],
  hint: "",
  logoUrl: "",
  accent: "#5ea3f7",
  accent2: "#a78bfa",
  direction: "auto",
  maxTiles: MAX_TILES,
  dashboardFallbackTitle: "",
  revealMs: 650,
  idleAfterSec: 0,
  showStatus: true,
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const MAX_CHIPS = 8;

function str(value: unknown, fallback: string, max = 400): string {
  return typeof value === "string" ? value.slice(0, max) : fallback;
}

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.trunc(Number(value));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

/**
 * Coerce untrusted JSON into a usable config. Never throws and never rejects.
 *
 * Same discipline as the robot's `normalize_tile`: a screen that refuses to
 * render because someone typed `maxTiles: 40` is worse than a screen showing
 * six tiles. Refusal with a sentence attached belongs in the console, where a
 * person is standing -- `validateScreenConfig` in `@console/shared` -- not on a
 * wall nobody is watching.
 */
export function normalizeScreenConfig(raw: unknown): ScreenConfig {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_SCREEN_CONFIG;
  const hex = (v: unknown, fallback: string) =>
    typeof v === "string" && HEX.test(v.trim()) ? v.trim().toLowerCase() : fallback;

  return {
    headline: str(o.headline, d.headline, 200),
    subheadline: str(o.subheadline, d.subheadline, 600),
    chips: Array.isArray(o.chips)
      ? o.chips.filter((c) => typeof c === "string" && c.trim()).map((c) => String(c).slice(0, 60)).slice(0, MAX_CHIPS)
      : d.chips,
    hint: str(o.hint, d.hint, 200),
    logoUrl: str(o.logoUrl, d.logoUrl, 2000),
    accent: hex(o.accent, d.accent),
    accent2: hex(o.accent2, d.accent2),
    direction: TEXT_DIRECTIONS.includes(o.direction as TextDirection)
      ? (o.direction as TextDirection)
      : d.direction,
    maxTiles: clamp(o.maxTiles, 1, MAX_TILES, d.maxTiles),
    dashboardFallbackTitle: str(o.dashboardFallbackTitle, d.dashboardFallbackTitle, 200),
    // Floor of 0 (instant) and a ceiling of 5s: past that a four-tile dashboard
    // takes longer to draw than the sentence introducing it.
    revealMs: clamp(o.revealMs, 0, 5000, d.revealMs),
    idleAfterSec: clamp(o.idleAfterSec, 0, 86_400, d.idleAfterSec),
    showStatus: typeof o.showStatus === "boolean" ? o.showStatus : d.showStatus,
  };
}

export const TILE_TYPES = ["kpi", "bar", "pie", "line", "table", "map"] as const;
export type TileType = (typeof TILE_TYPES)[number];

export interface MapMarker {
  label: string;
  lng: number;
  lat: number;
}

export interface Tile {
  /** Stable identity so React never re-mounts (and re-animates) an existing tile. */
  id: string;
  type: TileType;
  title: string;
  /** Category / metric names. Parallel to `values` (or `textValues` for tables). */
  labels: string[];
  /** Numbers, for every type except `table`. */
  values: number[];
  /** Free text, `table` only. Parallel to `labels`. */
  textValues?: string[];
  /** Suffix shown after formatted numbers, e.g. "طالب", "%", "ريال". */
  unit?: string;
  /** map only: map centre as [lng, lat]. */
  center?: [number, number];
  /** map only: initial zoom; ignored when markers are fitted instead. */
  zoom?: number;
  /** map only: pins to draw. */
  markers?: MapMarker[];
}

export type DisplayMode = "idle" | "dashboard" | "video" | "page" | "image" | "call";

export const CALL_STATUSES = ["incoming", "collecting", "confirming", "sending", "accepted", "failed", "unknown"] as const;
export interface ServiceCallState {
  id: string;
  status: (typeof CALL_STATUSES)[number];
  startedAt: number;
  answeredAt?: number;
  recipient: { name: string };
  fields: Partial<Record<"caller_name" | "phone" | "issue" | "school" | "location" | "route", string>>;
  detail: string;
}

export interface DisplayState {
  mode: DisplayMode;
  title: string;
  tiles: Tile[];
  video: { url: string; title: string; muted?: boolean; playbackId?: string } | null;
  page: { url: string; title: string } | null;
  image: { url: string; title: string } | null;
  call: ServiceCallState | null;
  robot: { online: boolean; speaking: boolean };
  /**
   * How this screen is dressed. Outlives every content event -- clearing the
   * display returns the wall to *this org's* idle screen, not to a blank one.
   */
  config: ScreenConfig;
}

export const initialState: DisplayState = {
  mode: "idle",
  title: "",
  tiles: [],
  video: null,
  page: null,
  image: null,
  call: null,
  robot: { online: false, speaking: false },
  config: DEFAULT_SCREEN_CONFIG,
};

/* ------------------------------------------------------------------- slides
 *
 * Not built, and shaped here so that building it is additive rather than a
 * refactor. The three insertion points, in order:
 *
 *   1. `"slides"` joins `DisplayMode`, and `slides: SlideDeck | null` joins
 *      `DisplayState`.
 *   2. `slides.show` and `slides.goto` join `DisplayEvent`, `applyEvent` and
 *      `parseEvent`. `slides.show` folds exactly as `video.show` does -- fills
 *      its own slot, nulls the others, empties `tiles` -- because one mode owns
 *      the screen at a time. `slides.goto` moves `index` and touches nothing
 *      else, so it must not drop the reveal queue in `useDisplay`.
 *   3. `App.tsx` grows one branch and a `SlidesScreen` component.
 *
 * The decision already taken: a slide carries a URL that came from the org's
 * content catalog (`ContentItem` of kind `image`), resolved on the robot from a
 * catalog id. The model picks *which* deck, never what a slide points at -- the
 * same rule that keeps `show_content` from browsing the open web.
 */
export interface Slide {
  imageUrl: string;
  caption?: string;
}

export interface SlideDeck {
  title: string;
  slides: Slide[];
  /** Zero-based position of the slide currently up. */
  index: number;
}

/** Everything the robot -- or, for `display.reload`, the relay -- can tell the screen to do. */
export type DisplayEvent =
  | { type: "call.update"; call: ServiceCallState }
  | { type: "dashboard.begin"; title: string }
  | { type: "dashboard.tile"; tile: Tile }
  | { type: "video.show"; url: string; title: string; muted?: boolean; playbackId?: string }
  | { type: "page.show"; url: string; title: string }
  | { type: "image.show"; url: string; title: string }
  | { type: "display.clear" }
  | { type: "robot.status"; online: boolean; speaking: boolean }
  /**
   * Relay -> panel only, from `POST /api/reload` (the console's "reload this
   * screen" button, §8.6). Carries no state; the panel reloads itself and
   * re-syncs. Folds to a no-op so a late joiner's `sync` is unaffected.
   */
  | { type: "display.reload" }
  /**
   * Relay -> panel only, from `POST /api/screen-config`: the console saved this
   * screen's appearance and the wall updates without a reload.
   *
   * Deliberately absent from `parseEvent`, which is the robot's door. A robot
   * that could publish this could rename the screen it is presenting on.
   */
  | { type: "screen.config"; config: ScreenConfig };

/** Relay -> panel. `sync` carries the whole state; the rest are incremental. */
export type ServerMessage =
  | { v: number; seq: number; type: "sync"; state: DisplayState }
  | ({ v: number; seq: number } & DisplayEvent);

/** Robot -> relay. */
export type ClientMessage = { v: number } & DisplayEvent;

/**
 * Fold one event into the state. Pure, and returns a new object only for the
 * parts that changed so React can bail out of untouched subtrees.
 */
export function applyEvent(state: DisplayState, ev: DisplayEvent): DisplayState {
  switch (ev.type) {
    case "call.update":
      return { ...state, mode: "call", call: ev.call, title: "", tiles: [], video: null, page: null, image: null };
    case "dashboard.begin":
      return { ...state, mode: "dashboard", call: null, title: ev.title, tiles: [], video: null, page: null, image: null };

    case "dashboard.tile": {
      // A tile can arrive without a preceding begin (Gemini calling add_tile
      // cold); treat that as an implicit begin rather than dropping it.
      const tiles = [...(state.mode === "dashboard" ? state.tiles : []), ev.tile];
      return {
        ...state,
        mode: "dashboard",
        call: null,
        video: null,
        page: null,
        image: null,
        // The per-screen ceiling, not the fleet one: a small panel by a lift
        // configured for three tiles drops the oldest at three.
        tiles: tiles.slice(-state.config.maxTiles),
      };
    }

    case "video.show":
      return { ...state, mode: "video", call: null, video: { url: ev.url, title: ev.title,
        muted: ev.muted === true, playbackId: ev.playbackId }, tiles: [], page: null, image: null };

    case "page.show":
      return { ...state, mode: "page", call: null, page: { url: ev.url, title: ev.title }, tiles: [], video: null, image: null };

    case "image.show":
      return { ...state, mode: "image", call: null, image: { url: ev.url, title: ev.title }, tiles: [], video: null, page: null };

    case "display.clear":
      return { ...state, mode: "idle", call: null, title: "", tiles: [], video: null, page: null, image: null };

    case "robot.status":
      return { ...state, robot: { online: ev.online, speaking: ev.speaking } };

    case "screen.config":
      // Config outlives content, so nothing else in the state is touched --
      // re-dressing a screen mid-presentation must not wipe the dashboard.
      // The new ceiling applies to the tiles already up, or lowering it would
      // only take effect on the next dashboard.
      return {
        ...state,
        config: ev.config,
        tiles: state.tiles.slice(-ev.config.maxTiles),
      };

    case "display.reload":
      // A command to the client, not a state change. The panel acts on it in
      // its message handler; the fold leaves state untouched so replaying it
      // into a late joiner's `sync` does nothing.
      return state;

    default:
      return state;
  }
}

/** Narrow untrusted JSON into a DisplayEvent. Returns null if it isn't one. */
export function parseEvent(raw: unknown): DisplayEvent | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  switch (o.type) {
    case "call.update": {
      const c = o.call as Record<string, unknown> | null;
      if (!c || typeof c !== "object" || typeof c.id !== "string" || !c.id ||
          !CALL_STATUSES.includes(c.status as ServiceCallState["status"]) ||
          typeof c.startedAt !== "number" || !Number.isFinite(c.startedAt) || c.startedAt <= 0) return null;
      const rawFields = c.fields && typeof c.fields === "object" ? c.fields as Record<string, unknown> : {};
      const fields: ServiceCallState["fields"] = {};
      for (const key of ["caller_name", "phone", "issue", "school", "location", "route"] as const) {
        if (typeof rawFields[key] === "string") fields[key] = rawFields[key].slice(0, key === "issue" ? 1500 : 200);
      }
      const recipient = c.recipient && typeof c.recipient === "object" ? c.recipient as Record<string, unknown> : {};
      return { type: "call.update", call: { id: c.id.slice(0, 128), status: c.status as ServiceCallState["status"],
        startedAt: c.startedAt,
        ...(typeof c.answeredAt === "number" && Number.isFinite(c.answeredAt) && c.answeredAt >= c.startedAt ? { answeredAt: c.answeredAt } : {}),
        fields, recipient: { name: str(recipient.name, "الفريق المختص", 100) }, detail: str(c.detail, "", 300) } };
    }
    case "dashboard.begin":
      return { type: "dashboard.begin", title: String(o.title ?? "") };
    case "dashboard.tile": {
      const tile = parseTile(o.tile);
      return tile ? { type: "dashboard.tile", tile } : null;
    }
    case "video.show":
      if (!o.url) return null;
      return { type: "video.show", url: String(o.url), title: String(o.title ?? ""),
        muted: o.muted === true,
        ...(typeof o.playbackId === "string" ? { playbackId: o.playbackId.slice(0, 128) } : {}) };
    case "page.show":
      if (!o.url) return null;
      return { type: "page.show", url: String(o.url), title: String(o.title ?? "") };
    case "image.show":
      if (!o.url) return null;
      return { type: "image.show", url: String(o.url), title: String(o.title ?? "") };
    case "display.clear":
      return { type: "display.clear" };
    case "display.reload":
      return { type: "display.reload" };
    case "robot.status":
      return { type: "robot.status", online: !!o.online, speaking: !!o.speaking };
    default:
      // Includes `screen.config` and `display.reload`, and that is the point:
      // this narrows what arrives on the *robot* socket, and those two are the
      // relay's to publish. A robot sending one is dropped and logged.
      return null;
  }
}

function parseTile(raw: unknown): Tile | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const type = o.type as TileType;
  if (!TILE_TYPES.includes(type)) return null;

  const tile: Tile = {
    id: String(o.id ?? `t${Date.now()}${Math.random().toString(36).slice(2, 6)}`),
    type,
    title: String(o.title ?? ""),
    labels: Array.isArray(o.labels) ? o.labels.map(String) : [],
    values: Array.isArray(o.values)
      ? o.values.map(Number).map((n) => (Number.isFinite(n) ? n : 0))
      : [],
  };
  if (Array.isArray(o.textValues)) tile.textValues = o.textValues.map(String);
  if (o.unit) tile.unit = String(o.unit);

  if (type === "map") {
    if (Array.isArray(o.markers)) {
      tile.markers = (o.markers as unknown[])
        .map(parseMarker)
        .filter((m): m is MapMarker => m !== null);
    }
    const c = o.center;
    if (Array.isArray(c) && c.length === 2 && c.every((n) => Number.isFinite(Number(n)))) {
      tile.center = [Number(c[0]), Number(c[1])];
    }
    if (Number.isFinite(Number(o.zoom))) tile.zoom = Number(o.zoom);
    // A map with neither pins nor a centre is a picture of the ocean.
    if (!tile.markers?.length && !tile.center) return null;
  } else if (tile.labels.length === 0) {
    return null;
  }
  return tile;
}

function parseMarker(raw: unknown): MapMarker | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const lng = Number(o.lng);
  const lat = Number(o.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { label: String(o.label ?? ""), lng, lat };
}
